// The shared core behind every drawer/card save controller (`agent-model-save.ts`,
// `agent-mode-save.ts`, `repo-route-save.ts`) — #5211 round 4.
//
// `agent-mode-save.ts` (mode writes) and `repo-route-save.ts` (repo-scope route
// pins) each COPIED `agent-model-save.ts`'s (#5210) send/lock/notify/refetch
// sequence, and the copies diverged from the original in two ways a round-3
// review caught:
//
//   (a) THE RE-READ RAN INSIDE THE LOCK. Both copies awaited their post-write
//       `refetch()` in the SAME `lock.run(key, …)` call as the write itself —
//       `finally { await input.refetch(); }`. `keyed-mutation.ts`'s lock is a
//       synchronous `Set` guard: a second call for the SAME key made while that
//       `finally` is still pending (Undo, clicked from the toast the write just
//       raised) hits `inFlight.has(key)` and is DROPPED — never sent, never even
//       refused. The original never had this bug: its re-read already ran AFTER
//       `writes.run(…)` had returned, with the lock already released.
//   (b) ONLY THE ORIGINAL CLEARED A STANDING REFUSAL ON AN ORG SWITCH (the
//       `on(activeAccountId, …, {defer:true})` effect that drops the page's
//       toast and every held refusal). The copies refuse a write MADE after the
//       switch — a tenant check at send time — but a refusal notice already up
//       when the switch happens was left standing, targeting the org the page
//       just left.
//
// THE FIX IS TO STOP COPYING: this module owns the keyed lock, the per-key
// refusal store, the org-switch cleanup effect and the tenant guard — once.
// `createModelSaves`, `createModeSaves` and `createRepoRouteSaves` are thin
// adapters over `createGuardedSaves`; each supplies only its own write.
//
// THE RE-READ ALWAYS RUNS OUTSIDE THE LOCK: `run`'s returned promise resolves
// the underlying `lock.run(…)` call (attempt done, lock released) BEFORE it
// calls `reread`. A same-key call made while `reread` is still in flight
// (Undo, a refusal's retry) is therefore never dropped — it takes the lock
// fresh, exactly like any other write. This is safe without a `settleRead`-
// style join (`agent-model-save.ts` keeps that machinery to itself, for its OWN
// optimistic cache paint — see that file's header) because a GUARDED write or
// a restore (Undo) carries the #5208 `expect` precondition: a second write
// racing an in-flight re-read is already carrying a version the server can
// prove stale the moment the first write's response lands, so it 409s rather
// than silently losing data. Undo itself is guarded by the version ITS OWN
// save just produced, never by a read.
//
// A FORCED REPLAY ("Apply mine anyway") carries no `expect` at all — that is
// what "force" means — so it has no version for the server to catch a race
// with. Its own safety comes from the OTHER end instead: each adapter's
// `replay` re-reads the row fresh, under this SAME lock, immediately before
// planning the write (`agent-model-save.ts`'s `send(..., reread: true)`,
// `repo-route-save.ts`'s round-5 `replay` over `fetchLlmRoutes`) — never a
// caller-supplied row that may already be the stale one the refusal proved
// wrong. The lock only keeps two attempts for the SAME key from overlapping;
// it is the re-read, not the lock, that keeps a forced write from being built
// over a guess.

import { createEffect, on, untrack } from 'solid-js';
import { createStore } from 'solid-js/store';
import { activeAccountId } from './active-account';
import { createKeyedMutation } from './keyed-mutation';
import { orgFromPath } from './org';
import type { RouteRefusal } from './route-refusal';

/**
 * The tenant a write goes to, both halves: the org the page's cache/query is
 * keyed on (`activeAccountId`) and the org `apiFetch` sends as `X-Dz-Account`
 * (the URL's). `OrgScope` sets them together; a write refuses if EITHER moved
 * since its intent was made.
 */
export type Tenant = { readonly account: string | null; readonly header: string | null };

/** The page's tenant right now. Untracked: a read at a click, never a subscription. */
export function tenantNow(): Tenant {
  return {
    account: untrack(activeAccountId),
    header: typeof window === 'undefined' ? null : orgFromPath(window.location.pathname),
  };
}

/** The SAME "did the page move to another org" check every write below guards
 * its send, and its Undo, with. */
export const sameTenant = (a: Tenant, b: Tenant): boolean =>
  a.account === b.account && a.header === b.header;

/** A refused save, and the intent it refused — what a notice's retry replays. */
export type PendingRefusal<I> = { readonly refusal: RouteRefusal; readonly intent: I };

/** What one attempt may do to its own key's refusal while it holds the lock. */
export type GuardedSaveCtx = {
  /** True once the page has moved to another org since the intent was made. */
  readonly moved: () => boolean;
  /**
   * If the page has moved since the intent was made, hold `account_changed`
   * against this key and report `true` — a mid-attempt beat (after an
   * `await`) needs its OWN check: the top-level one in `run` only catches a
   * switch that happened before `attempt` was even called. A no-op (`false`)
   * when the tenant still matches.
   */
  readonly refuseMoved: () => boolean;
  /**
   * Hold `refusal` against this key, unless the page has since moved — a
   * write (or its notify) that landed after a switch stands on the OTHER
   * org's card, never retrying into this one. (The `moved` case itself is
   * {@link refuseMoved}, which sets exactly when it IS moved — the opposite
   * guard.)
   */
  readonly refuse: (refusal: RouteRefusal) => void;
  /** Drop this key's standing refusal — a fresh attempt starting, or a write
   * that is about to succeed. */
  readonly clearRefusal: () => void;
};

export type GuardedSaves<I> = {
  /** True while THIS key has an attempt in flight. */
  readonly isBusy: (key: string) => boolean;
  /** The key's standing refusal, or null. */
  readonly refusal: (key: string) => PendingRefusal<I> | null;
  readonly dismiss: (key: string) => void;
  /**
   * Run one attempt for `key`, guarded by `intent.tenant`: refused
   * `account_changed`, unsent, if the page has already moved before `attempt`
   * even starts; else runs `attempt` under the key's lock. `reread` always
   * runs after the lock is released (see file header), skipped only if the
   * page moved meanwhile.
   */
  readonly run: (
    key: string,
    intent: I,
    attempt: (ctx: GuardedSaveCtx) => Promise<void>,
  ) => Promise<void>;
};

export function createGuardedSaves<I extends { readonly tenant: Tenant }>(input: {
  readonly fallback: string;
  /** Take the page's ONE toast down on an org switch (see file header). */
  readonly dismissNotice: () => void;
  /** Re-read after an attempt, win or lose — run OUTSIDE the lock. */
  readonly reread: (intent: I) => Promise<unknown>;
}): GuardedSaves<I> {
  const lock = createKeyedMutation({ fallback: input.fallback });
  const [refusals, setRefusals] = createStore<Record<string, PendingRefusal<I> | undefined>>({});

  // The second layer: an org switch takes down the page's toast and every
  // standing refusal — a retry belongs to the org it was refused in. `defer`:
  // the first run is the mount, not a switch.
  createEffect(
    on(
      activeAccountId,
      () => {
        input.dismissNotice();
        for (const key of Object.keys(refusals)) setRefusals(key, undefined);
      },
      { defer: true },
    ),
  );

  const run = async (
    key: string,
    intent: I,
    attempt: (ctx: GuardedSaveCtx) => Promise<void>,
  ): Promise<void> => {
    const moved = (): boolean => !sameTenant(intent.tenant, tenantNow());
    const ctx: GuardedSaveCtx = {
      moved,
      refuseMoved: () => {
        if (!moved()) return false;
        setRefusals(key, { refusal: { kind: 'account_changed' }, intent });
        return true;
      },
      refuse: (refusal) => {
        if (!moved()) setRefusals(key, { refusal, intent });
      },
      clearRefusal: () => setRefusals(key, undefined),
    };
    // `lock.run`'s `call` is `attempt(ctx)`'s OWN promise, not a second async
    // function that awaits it: an extra wrapping layer adds a microtask hop
    // between the refusal `attempt` sets on its way out and `keyed-mutation`'s
    // `finally` clearing `isBusy` — long enough, once, to leave the refusal
    // notice's own retry button rendered but still disabled. Matching the
    // ORIGINAL (pre-round-4) shape here — the write's catch block sat directly
    // in the function `writes.run` awaited, zero extra hops — is why this is a
    // plain function, not `async () => { ...; await attempt(ctx); }`.
    await lock.run(key, () => {
      // The tenant may have moved before `attempt` was even called (a switch
      // during the settled-mutation queue, or between the caller building
      // `intent` and the lock actually being free).
      if (ctx.refuseMoved()) return Promise.resolve();
      return attempt(ctx);
    });
    // Released the lock above: a same-key call made from here on (Undo, a
    // retry) takes it fresh rather than finding it still held by `reread`.
    if (moved()) return;
    await input.reread(intent);
  };

  return {
    isBusy: lock.isBusy,
    refusal: (key) => refusals[key] ?? null,
    dismiss: (key) => setRefusals(key, undefined),
    run,
  };
}
